import { Controller } from "@hotwired/stimulus";

const drawerOptions = {
  placement: "right",
  backdrop: false,
  bodyScrolling: false,
  edge: false,
  onHide: () => {},
  onShow: () => {},
  onToggle: () => {},
};

const drawerInstanceOptions = {
  id: "security-drawer",
  override: true,
};

export default class extends Controller {
  static targets = [];

  securityDrawer;
  tableReady = false;

  connect() {
    // Initialize drawer only if the element exists
    const drawerElement = document.getElementById("security-drawer");
    if (drawerElement && window.Drawer) {
      // Remove hidden class to allow Drawer library to initialize properly
      drawerElement.classList.remove('hidden');

      this.securityDrawer = new Drawer(
        drawerElement,
        drawerOptions,
        drawerInstanceOptions
      );
      this.securityDrawer.hide();
    }

    // Update button tooltip to show loading state
    this.updateButtonTooltip('Loading...');

    // Listen for turbo frame load event to know when table is ready
    document.addEventListener('turbo:frame-load', (event) => {
      if (event.target.id === 'securities_data') {
        // Wait for the table to actually render, not just the frame to load
        this.waitForTableRendering();
      }
    });

    // Initialize selected count
    this.updateSelectedCount();

    // Listen for selection changes
    document.addEventListener('securities-selection-changed', () => {
      this.updateSelectedCount();
    });
  }

  waitForTableRendering() {
    let attempts = 0;
    const maxAttempts = 100; // 10 seconds maximum

    const checkTableRendering = () => {
      attempts++;

      // Check if the loading content has been replaced with actual table content
      const frameContent = document.getElementById('securities_data');
      const loadingText = frameContent?.querySelector('p.text-neutral-500');
      const loadingContainer = frameContent?.querySelector('.flex.flex-col.items-center.justify-start.space-y-4.p-8.min-h-\\[500px\\]');

      // Check if the table is rendered
      const tableElement = document.getElementById('security-table');
      const tableController = document.querySelector('[data-controller*="pages--security--security-table"]');

      // Table is ready when:
      // 1. Loading content is gone (replaced by actual table content)
      // 2. Table element exists
      // 3. Table controller exists
      const loadingGone = !loadingText && !loadingContainer;
      const tableExists = tableElement && tableController;

      if (loadingGone && tableExists) {
        this.tableReady = true;
        this.updateButtonTooltip('Filters');
      } else if (attempts >= maxAttempts) {
        this.tableReady = true;
        this.updateButtonTooltip('Filters');
      } else {
        // Check again in 100ms
        setTimeout(checkTableRendering, 100)
      }
    }

    // Start checking
    checkTableRendering()
  }

  openFiltersDrawer() {
    // Only open drawer if table is ready
    if (!this.tableReady) {
      return;
    }

    this.securityDrawer.show()

    // Dispatch event to notify that drawer is opened
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('security-drawer-opened'))
    }, 100)
  }

  closeFiltersDrawer() {
    this.securityDrawer.hide()
  }

  createSecurityList(event) {
    event.preventDefault();

    // Get the ISINs from local storage
    let savedSelectedRows = JSON.parse(localStorage.getItem("selectedRows") || "[]");

    // Show a simple prompt for now - could be enhanced with a modal
    var securityListName = prompt("Enter security list name:");

    if (securityListName === null) {
      return; // User cancelled the prompt
    }

    if (!securityListName || securityListName.trim() === "") {
      securityListName = "New Security List";
    }

    // Extract ISINs from the selected rows
    const isins = JSON.parse(localStorage.getItem("selectedRows") || "[]");

    if (isins.length === 0) {
      alert("Please select at least one security to add to the security list.");
      return;
    }

    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    // Send JSON request to create security list
    fetch('/security_lists', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        security_list: {
          name: securityListName,
          isins: isins
        }
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Clear localStorage on successful creation
        localStorage.removeItem("selectedRows");

        // Dispatch custom event for selection changes
        document.dispatchEvent(new CustomEvent('securities-selection-changed', {
          detail: { selectedRows: [] }
        }));

        // Redirect to securities page with security list parameter
        window.location.href = `/securities?security_list=${data.security_list_id}`;
      } else {
        // Show error message
        alert(`Error creating security list: ${data.errors}`);
      }
    })
    .catch(error => {
      console.error('Error creating security list:', error);
      alert('Error creating security list. Please try again.');
    });
  }

  createWatchlist(event) {
    event.preventDefault();

    // Show a simple prompt for watch list name
    var watchListName = prompt("Enter watch list name:");

    if (watchListName === null) {
      return; // User cancelled the prompt
    }

    if (!watchListName || watchListName.trim() === "") {
      watchListName = "New Watch List";
    }

    // Get current filter parameters from URL or form
    const urlParams = new URLSearchParams(window.location.search);
    const filters = {};

    // First try to get filters from URL parameters
    // Handle multiple values for the same parameter
    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith('q[') && value) {
        if (filters[key]) {
          // If key already exists, convert to array and add new value
          if (Array.isArray(filters[key])) {
            filters[key].push(value);
          } else {
            filters[key] = [filters[key], value];
          }
        } else {
          filters[key] = value;
        }
      }
    }

    // If no URL filters, try to get from form
    if (Object.keys(filters).length === 0) {
      const form = document.getElementById('securities-form');
      if (form) {
        const formData = new FormData(form);
        for (let [key, value] of formData.entries()) {
          if (key.startsWith('q[') && value) {
            filters[key] = value;
          }
        }
      }
    }

    // Check if any filters are present
    if (Object.keys(filters).length === 0) {
      alert("Please select at least one filter to create a watch list.");
      return;
    }

    // Get CSRF token
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    // Send JSON request to create watch list
    fetch('/watch_lists', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({
        watch_list: {
          name: watchListName,
          filters: filters
        }
      })
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        // Redirect to securities page with the new watch list ID
        window.location.href = `/securities?watch_list=${data.watch_list_id}`;
      } else {
        // Show error message
        alert(`Error creating watch list: ${data.errors}`);
      }
    })
    .catch(error => {
      console.error('Error creating watch list:', error);
      alert('Error creating watch list. Please try again.');
    });
  }

  updateSelectedCount() {
    const selectedRows = JSON.parse(localStorage.getItem("selectedRows") || "[]");

    // Update quote selection counter
    const countElement = document.getElementById("selected-count-helper");
    if (countElement) {
      countElement.textContent = `(${selectedRows.length})`;
    }

    // Update security list counter
    const securityListCountElement = document.getElementById("security-list-count-helper");
    if (securityListCountElement) {
      securityListCountElement.textContent = `(${selectedRows.length})`;
    }

    // Update quote selected option visibility
    const quoteSelectedOptions = document.querySelectorAll('[data-quote-selected-option="true"]');
    quoteSelectedOptions.forEach(option => {
      if (selectedRows.length === 0) {
        option.style.display = 'none';
      } else {
        option.style.display = 'block';
      }
    });
  }

  createQuoteWithSelected(event) {
    event.preventDefault();

    const selectedRows = JSON.parse(localStorage.getItem("selectedRows") || "[]");

    // Dispatch custom event to open the quote creation modal
    document.dispatchEvent(new CustomEvent('open-quote-creation-modal', {
      detail: {
        selectedSecurityIds: selectedRows,
        selectedIsins: selectedRows.map(isin => isin.toUpperCase())
      }
    }));
  }

  createQuoteFromScratch(event) {
    event.preventDefault();
    window.location.href = `/oms/quotes/new`;
  }

  updateButtonTooltip(tooltip) {
    const drawerButton = document.querySelector('[data-action*="openFiltersDrawer"]');
    if (drawerButton) {
      // Update the title attribute
      drawerButton.title = tooltip;

      // Find the tooltip div and update its content
      const tooltipTarget = drawerButton.getAttribute('data-tooltip-target');
      if (tooltipTarget) {
        const tooltipDiv = document.getElementById(tooltipTarget);
        if (tooltipDiv) {
          // Update the tooltip text content
          const tooltipText = tooltipDiv.querySelector('.tooltip-arrow')?.previousSibling;
          if (tooltipText && tooltipText.nodeType === Node.TEXT_NODE) {
            tooltipText.textContent = tooltip;
          } else {
            // If the structure is different, try to update the first text node
            const textNodes = Array.from(tooltipDiv.childNodes).filter(node => node.nodeType === Node.TEXT_NODE);
            if (textNodes.length > 0) {
              textNodes[0].textContent = tooltip;
            }
          }
        }
      }

      // Add visual feedback based on state
      if (tooltip === 'Loading...') {
        drawerButton.classList.add('opacity-50');
        // Add a subtle loading animation
        const icon = drawerButton.querySelector('i');
        if (icon) {
          icon.classList.add('animate-pulse');
        }
      } else {
        drawerButton.classList.remove('opacity-50');
        // Remove loading animation
        const icon = drawerButton.querySelector('i');
        if (icon) {
          icon.classList.remove('animate-pulse');
        }
      }
    }
  }
}
